/**
 * Les élèves du tuteur, hors live : la ligne de Mes élèves (`GET api/students`) et la fiche d'un élève
 * (`GET api/students/{id}`, puis son brief `GET api/students/{id}/brief`).
 *
 * Portage littéral de l'ancien écran, chaque champ à la lecture qu'il en faisait :
 *  - la ligne : `tutor/students/StudentsListItem.js` (sans options, la ligne de `MyStudentsTabScreen`) ;
 *  - la fiche : `tutor/students/StudentProfil.js`, `ProfilDetails.js`, `StudentBriefSections.js`,
 *    `common/account/ObjectivesSummaryDisplay.js` (lecture), `StatisticsList.js`, `SkillSetTitleButton.js`.
 *
 * Écarts voulus avec la fiche du live (`adapters/tutor/Live/tutorLiveStudentProfileAdapter.ts`), qui n'écrit
 * que l'Aperçu : ici le libellé d'un objectif sans matière est celui de l'ancienne fiche (« Objectif
 * pédagogique »), et les dates du brief sont au format de l'ancienne fiche (`formatDate`, « 20 octobre
 * 2026 »). Le live garde les siens : on ne touche pas sa salle.
 *
 * Ce que le serveur ne sert pas, l'ancien écran ne le montrait pas non plus : la liste n'est chargée
 * qu'avec le profil (`StudentController@index`), donc ni objectifs, ni compteurs d'activités ou de
 * rapports — la ligne garde ses matières prioritaires, sans compte. Ces formes restent lues quand elles
 * arrivent (l'élève relu par `getStudent`, que le réducteur recopie dans la liste).
 */
import moment from "moment"

import { championSlugsToNames } from "../../../../../data/constants/championsConstants"
import { offersNames } from "../../../../../data/utility/ability"
import { type ApiSchoolSubject, toUISubject } from "../../parent/shared/schoolSubjectAdapter"
import { formatFrenchDe } from "../../student/shared/questAdapter"
import { formatDayMonthYear } from "../../../ui/formatters/dateFormatter"
import { dayLabelOf } from "../shared/tutorWeekAdapter"
import { type UISchoolAssessment } from "../../../ui/types/schoolAssessment"
import { type UISubject } from "../../../ui/types/schoolSubject"
import {
    type UITutorSessionBrief,
    type UITutorStudent,
    type UITutorStudentDetail,
    type UITutorStudentGame,
    type UITutorStudentObjective,
    type UITutorStudentProfile,
} from "../../../ui/types/tutorEleves"

type Id = string | number

type ApiSubjectRef = (ApiSchoolSubject & { specialty?: boolean | null }) | null

export type ApiTutorObjective = {
    id?: Id | null
    type?: string | null
    status?: string | null
    subject_ids?: Id[] | null
    allocated_hours?: number | string | null
    live_sessions?: ({ id?: Id | null; day_time?: string | null } | null)[] | null
}

/** Un élève du tuteur, tel que `StudentResource` le sert (liste ou fiche). */
export type ApiTutorStudent = {
    id?: Id | null
    display_name?: string | null
    firstname?: string | null
    lastname?: string | null
    avatar?: string | null
    created_at?: string | null
    timezone?: string | null
    channel?: string | null
    reason?: string | null
    email?: string | null
    parent_email?: string | null
    mobile?: string | null
    parent_mobile?: string | null
    xp?: number | null
    rank?: number | null
    has_study_program?: boolean | null
    current_request_precisions?: string | null
    current_offer?: string | null
    subscription_plan_display?: string | null
    subscription_duration_label?: string | null
    prioritized_school_subjects?: ApiSubjectRef[] | null
    school_subjects?: ApiSubjectRef[] | null
    appreciated_school_subjects?: ApiSubjectRef[] | null
    address?: { city?: string | null } | null
    profile?: {
        class?: string | null
        special_needs?: string[] | null
        desired_support?: string | null
        subject_priority?: string | null
        subject_preference?: string | null
        school_name?: string | null
        qualities?: string[] | null
        future_job?: string | null
        future_orientation?: string | null
        personal_work?: { duration?: string | null; frequency?: string | null } | null
    } | null
    pedagogical_objectives?: (ApiTutorObjective | null)[] | null
    skill_sets?:
        | ({
              id?: Id | null
              champion?: string | null
              current_step?: number | null
              steps?: ({ title_gained?: string | null } | null)[] | null
          } | null)[]
        | null
    title_skill_set_id?: Id | null
    shop_purchases?: ({ shop_product_id?: Id | null } | null)[] | null
    /** Lus par l'ancienne ligne ; le serveur ne les sert pas aujourd'hui dans la liste. */
    statistics?: { today?: { activities?: { count?: number | null } | null } | null } | null
    count_today_done_activities?: number | null
    live_reports?: unknown[] | null
}

/** Une séance du tuteur (`currentUser.live_sessions`) : ses élèves et leurs non-lus. */
export type ApiTutorOwnSession = {
    students?: ({ id?: Id | null } | null)[] | null
    unread_private_messages_count?: Record<string, number | null | undefined> | unknown[] | null
}

/** Le brief (`StudentBriefResource`) : le profil et la séance, chacun un récit et ses données. */
export type ApiTutorStudentBrief = {
    profile?: {
        content?: { profile_narrative?: string | null } | null
        structured_data?: { session_count?: number | null } | null
    } | null
    session?: {
        content?: { session_narrative?: string | null } | null
        structured_data?: {
            objective?: { subjects?: ({ name?: string | null } | null)[] | null } | null
            last_session?: {
                date?: string | null
                tutor_tags?: ({ type?: string | null; label?: string | null } | null)[] | null
                tutor_comment?: string | null
            } | null
            assessments?:
                | ({
                      id?: Id | null
                      title?: string | null
                      type?: string | null
                      subject?: string | null
                      date?: string | null
                      date_label?: string | null
                      days_until?: number | null
                      urgency?: string | null
                      chapters?: string[] | null
                  } | null)[]
                | null
            starting_block?: {
                chapitre?: string | null
                subject_name?: string | null
                assessment_declared?: boolean | null
                assessment_date?: string | null
                summary?: string | null
            } | null
        } | null
    } | null
}

/** Ce que la lecture prend dans le store, à côté de l'élève. */
export type TutorStudentContext = {
    subjectsById: Record<string, ApiSchoolSubject>
    /** `staticData.student_special_needs` : slug → libellé. */
    specialNeedLabels?: Record<string, string> | null
    /** `staticData.personal_work_form` : les deux libellés du travail personnel. */
    personalWorkForm?: {
        duration?: { title?: string | null } | null
        frequency?: { title?: string | null } | null
    } | null
    /** `formConstants.subjectPriorities` : valeur → libellé. */
    subjectPriorities?: readonly { value?: string | null; label?: string | null }[]
    /** Les séances du tuteur, pour les non-lus de chaque élève. */
    tutorSessions?: (ApiTutorOwnSession | null)[] | null
    /** Le tuteur est relecteur ou admin (`review_activities`, `backOffice`) : les coordonnées. */
    canSeeContacts?: boolean
}

/** Le jour d'une séance, comme l'ancienne carte des objectifs (`ObjectivesSummaryDisplay.formatDayTime`) : « Mar 17:00 ». */
export function formatObjectiveDayTime(dayTime: string | null | undefined): string {
    if (!dayTime) return ""
    const parts = dayTime.split(" ")
    if (parts.length < 2) return dayTime
    return `${dayLabelOf(parts[0]!).slice(0, 3)} ${parts[1]}`
}

/** `formatDate` de `data/utility/Utils.js` : « 20 octobre 2026 », « - » sans date. */
const longDate = (value: string | null | undefined) => (value ? formatDayMonthYear(value) : "-")

const nonEmpty = (value: string | null | undefined): string | null =>
    value != null && String(value).trim() !== "" ? String(value) : null

function subjectRefs(refs: ApiSubjectRef[] | null | undefined, ctx: TutorStudentContext): UISubject[] {
    return (refs ?? [])
        .filter((ref): ref is NonNullable<ApiSubjectRef> => ref != null)
        .map((ref) => toUISubject({ ...(ref.id != null ? ctx.subjectsById[String(ref.id)] : undefined), ...ref }))
}

/**
 * Les matières de la ligne et de la carte « Matières » (`PrioritizedSchoolSubjects`) : les prioritaires ;
 * sans elles, « Aide aux devoirs » quand c'est l'accompagnement choisi ; sinon rien (« - »).
 */
function prioritizedSubjects(student: ApiTutorStudent, ctx: TutorStudentContext): UISubject[] {
    const prioritized = subjectRefs(student.prioritized_school_subjects, ctx)
    if (prioritized.length > 0) return prioritized
    if (student.profile?.desired_support === "Aide aux devoirs") {
        return [{ id: "aide-aux-devoirs", name: "Aide aux devoirs", color: "subject-default" }]
    }
    return []
}

/**
 * Les non-lus d'un élève, lus là où l'ancienne ligne les lisait : la première séance du tuteur qui le
 * compte parmi ses élèves (`unread_private_messages_count[id]`).
 */
export function unreadMessagesOf(studentId: Id, sessions: TutorStudentContext["tutorSessions"]): number {
    const session = (sessions ?? []).find((item) =>
        (item?.students ?? []).some((student) => String(student?.id) === String(studentId))
    )
    const counts = session?.unread_private_messages_count
    if (counts == null || Array.isArray(counts)) return 0
    const count = (counts as Record<string, number | null | undefined>)[String(studentId)]
    return typeof count === "number" && count > 0 ? count : 0
}

/** Qui est l'élève, partout où le tuteur le désigne (une ligne, le choix des élèves d'une activité commune). */
export function tutorStudentIdentity(
    student: ApiTutorStudent | null | undefined
): Pick<UITutorStudent, "id" | "name" | "avatarUrl" | "classLabel"> | null {
    if (student?.id == null) return null
    return {
        id: student.id,
        name: student.display_name ?? [student.firstname, student.lastname].filter(Boolean).join(" "),
        avatarUrl: student.avatar ?? undefined,
        classLabel: student.profile?.class ?? null,
    }
}

/** La ligne de Mes élèves. `null` sans id. */
export function toUITutorStudent(
    student: ApiTutorStudent | null | undefined,
    ctx: TutorStudentContext
): UITutorStudent | null {
    const identity = tutorStudentIdentity(student)
    if (identity == null || student == null) return null
    const today = student.count_today_done_activities ?? student.statistics?.today?.activities?.count
    return {
        ...identity,
        subjects: prioritizedSubjects(student, ctx),
        todayActivitiesCount: typeof today === "number" && today > 0 ? today : undefined,
        liveReportsCount: Array.isArray(student.live_reports) ? student.live_reports.length : undefined,
        unreadMessagesCount: unreadMessagesOf(identity.id, ctx.tutorSessions),
    }
}

/** Le libellé d'un objectif, celui de l'ancienne carte (`ObjectivesSummaryDisplay.buildObjectiveLabel`). */
function objectiveLabel(objective: ApiTutorObjective, subjects: UISubject[]): string {
    if (subjects.length === 0) return "Objectif pédagogique"
    const head = objective.type === "homework" ? "Aide aux devoirs" : "Améliorer son niveau"
    return `${head} en ${subjects.map((subject) => subject.name).join(", ")}`
}

/** Ses objectifs non clos (`status !== "closed"`), « EN COURS » ou « DEMANDÉ », heures et séances de la semaine. */
function toTutorStudentObjectives(student: ApiTutorStudent, ctx: TutorStudentContext): UITutorStudentObjective[] {
    return (student.pedagogical_objectives ?? [])
        .filter((objective): objective is ApiTutorObjective => objective?.id != null && objective.status !== "closed")
        .map((objective) => {
            const subjects = (objective.subject_ids ?? [])
                .map((id) => ctx.subjectsById[String(id)])
                .filter((subject): subject is ApiSchoolSubject => subject != null)
                .map((subject) => toUISubject(subject))
            const status = (objective.status ?? "").toLowerCase()
            const hours = objective.allocated_hours
            return {
                id: objective.id!,
                title: objectiveLabel(objective, subjects),
                subjects,
                hoursLabel: hours != null && hours !== "" ? `${hours}h/semaine` : null,
                schedule: (objective.live_sessions ?? [])
                    .map((session) => formatObjectiveDayTime(session?.day_time))
                    .filter((label) => label !== ""),
                status: status === "pending" ? "requested" : status === "ongoing" ? "ongoing" : null,
            }
        })
}

const URGENCIES = new Set<UISchoolAssessment["urgency"]>(["low", "medium", "high", "critical"])

/** Le brief de la séance (`BriefSessionSection`), aux mots et aux dates de l'ancienne fiche. */
function toTutorStudentSessionBrief(
    brief: ApiTutorStudentBrief | null | undefined,
    multiObjective: boolean,
    ctx: TutorStudentContext
): UITutorSessionBrief | null {
    const data = brief?.session?.structured_data
    if (data == null) return null
    const focus = brief?.session?.content?.session_narrative ?? null
    const objectiveSubjects = data.objective?.subjects ?? []
    // `subjectLabel` de l'ancienne fiche : la matière, seulement quand l'objectif n'en a qu'une.
    const subjectLabel = objectiveSubjects.length === 1 ? (objectiveSubjects[0]?.name ?? null) : null
    const subjectsByName = new Map(
        Object.values(ctx.subjectsById).map((subject) => [subject.name ?? "", subject] as const)
    )
    const last = data.last_session
    const starting = data.starting_block
    // Un brief servi sans rien à dire (un élève neuf : ni séance, ni contrôle, ni récit) est un brief absent :
    // l'ancienne fiche posait alors une section « Brief séance » vide à côté des objectifs.
    if (last == null && (data.assessments ?? []).length === 0 && starting == null && nonEmpty(focus) == null) {
        return null
    }
    return {
        nextSessionLabel: null,
        nextSessionSubjectLabel: subjectLabel,
        lastSession:
            last != null
                ? {
                      label: multiObjective && subjectLabel ? `Dernière séance en ${subjectLabel}` : "Dernière séance",
                      dateLabel: longDate(last.date),
                      tags: (last.tutor_tags ?? [])
                          .filter((tag): tag is { type?: string | null; label?: string | null } => tag?.label != null)
                          .slice(0, 3)
                          .map((tag) => ({
                              label: tag.label!,
                              tone: tag.type === "positive" ? "positive" : "negative",
                          })),
                      comment: nonEmpty(last.tutor_comment),
                  }
                : null,
        assessments: (data.assessments ?? [])
            .filter((assessment): assessment is NonNullable<typeof assessment> => assessment != null)
            .map((assessment, index) => {
                const urgency = (assessment.urgency ?? "") as UISchoolAssessment["urgency"]
                const subject = assessment.subject
                    ? toUISubject(subjectsByName.get(assessment.subject) ?? { name: assessment.subject })
                    : toUISubject({ name: "" })
                return {
                    id: assessment.id ?? `brief-assessment-${index}`,
                    // « Interro — Maths » de l'ancienne ligne, à ses deux morceaux.
                    title: assessment.title || assessment.type || "Contrôle",
                    date: assessment.date ?? "",
                    subject,
                    chaptersProgress: [],
                    urgency: URGENCIES.has(urgency) ? urgency : "low",
                    chapterNames: (assessment.chapters ?? []).filter((name) => nonEmpty(name) != null),
                }
            }),
        startingBlock:
            starting != null
                ? {
                      wantsToWork:
                          starting.chapitre && starting.subject_name
                              ? `${starting.chapitre} en ${starting.subject_name}`
                              : null,
                      assessmentDeclared: starting.assessment_declared
                          ? `A déclaré un contrôle${starting.assessment_date ? ` le ${longDate(starting.assessment_date)}` : ""}`
                          : null,
                      summary: nonEmpty(starting.summary),
                  }
                : null,
        focus: nonEmpty(focus),
    }
}

/** « Le jeu » : le titre et ses quêtes (`SkillSetTitleButton`), puis `StatisticsList`. */
function toGame(student: ApiTutorStudent): UITutorStudentGame {
    const skillSet = (student.skill_sets ?? []).find(
        (set) => set?.id != null && String(set.id) === String(student.title_skill_set_id)
    )
    const step = skillSet?.current_step ?? null
    const steps = skillSet?.steps ?? []
    const purchases = student.shop_purchases ?? []
    const countOf = (productId: number) =>
        purchases.filter((purchase) => String(purchase?.shop_product_id) === String(productId)).length
    return {
        title: step != null ? (steps[step - 1]?.title_gained ?? null) : null,
        // « Quêtes de <champion> finies : 3/5 », mot pour mot.
        questsLabel:
            skillSet != null && step != null
                ? `Quêtes ${formatFrenchDe(
                      (championSlugsToNames as Record<string, string>)[skillSet.champion ?? ""]
                  )} finies : ${step - 1}/${steps.length - 1}`
                : null,
        xp: student.xp ?? null,
        rank: student.rank ?? null,
        trees: countOf(1),
        meals: countOf(2),
    }
}

/** « Son dossier », ligne à ligne, dans l'ordre et aux mots de `ProfilDetails`. */
export function toTutorStudentDetails(student: ApiTutorStudent, ctx: TutorStudentContext): UITutorStudentDetail[] {
    const profile = student.profile ?? {}
    const priority = (value: string | null | undefined) =>
        value ? (ctx.subjectPriorities?.find((item) => item?.value === value)?.label ?? null) : null
    const isUpperClass = profile.class === "1ère" || profile.class === "Tle"
    const rows: (UITutorStudentDetail | null)[] = [
        { label: "Fuseau horaire", group: "request", value: nonEmpty(student.timezone) },
        { label: "Comment j'ai connu Masteur", group: "request", value: nonEmpty(student.channel) },
        { label: "Classe", group: "school", value: nonEmpty(profile.class) },
        { label: "Accompagnement souhaité", group: "request", value: nonEmpty(profile.desired_support) },
        { label: "Matière(s) choisie(s)", group: "request", subjects: prioritizedSubjects(student, ctx) },
        { label: "Masteur peut m'être utile pour", group: "request", value: nonEmpty(student.reason) },
        {
            label: "Matières où j'ai le plus besoin de m'améliorer",
            group: "request",
            value: priority(profile.subject_priority),
        },
        {
            label: "J'ai une préférence pour les matières",
            group: "request",
            value: priority(profile.subject_preference),
        },
        { label: "Ville", group: "school", value: nonEmpty(student.address?.city) },
        { label: "Etablissement scolaire", group: "school", value: nonEmpty(profile.school_name) },
        isUpperClass
            ? {
                  label: "Mes matières de spécialité",
                  group: "school",
                  subjects: subjectRefs(
                      (student.school_subjects ?? []).filter((subject) => subject?.specialty),
                      ctx
                  ),
              }
            : null,
        { label: "Mes principales qualités", group: "portrait", chips: (profile.qualities ?? []).filter(Boolean) },
        {
            label: "Les matières que j'apprécie le plus",
            group: "portrait",
            subjects: subjectRefs(student.appreciated_school_subjects, ctx),
        },
        { label: "Le(s) métier(s) où je m'imagine plus tard", group: "portrait", value: nonEmpty(profile.future_job) },
        { label: "L'orientation que je souhaiterais", group: "portrait", value: nonEmpty(profile.future_orientation) },
    ]
    // Le travail personnel : ses deux libellés viennent de `static-data` ; sans eux, l'ancienne fiche
    // écrivait une ligne sans titre — on ne la pose pas.
    const durationTitle = nonEmpty(ctx.personalWorkForm?.duration?.title)
    const frequencyTitle = nonEmpty(ctx.personalWorkForm?.frequency?.title)
    if (durationTitle) {
        rows.push({ label: durationTitle, group: "portrait", value: nonEmpty(profile.personal_work?.duration) })
    }
    if (frequencyTitle) {
        rows.push({ label: frequencyTitle, group: "portrait", value: nonEmpty(profile.personal_work?.frequency) })
    }
    return rows.filter((row): row is UITutorStudentDetail => row != null)
}

/** La fiche d'un élève. `null` tant que l'élève n'est pas connu. */
export function toUITutorStudentProfile(
    student: ApiTutorStudent | null | undefined,
    brief: ApiTutorStudentBrief | null | undefined,
    ctx: TutorStudentContext
): UITutorStudentProfile | null {
    const row = toUITutorStudent(student, ctx)
    if (row == null || student == null) return null
    const labels = ctx.specialNeedLabels ?? {}
    const sessionCount = brief?.profile?.structured_data?.session_count ?? null
    const objectives = toTutorStudentObjectives(student, ctx)
    const offer = student.current_offer
        ? ((offersNames as Record<string, string>)[student.current_offer.toLowerCase()] ?? null)
        : null
    return {
        student: row,
        sessionCount,
        memberSinceLabel: student.created_at ? moment(student.created_at).format("MMM YYYY") : null,
        isNew: sessionCount != null && sessionCount <= 3,
        specialNeeds: (student.profile?.special_needs ?? [])
            .filter((slug) => labels[slug] != null)
            .map((slug) => labels[slug]!),
        planLabel: nonEmpty(student.subscription_plan_display),
        planDurationLabel: nonEmpty(student.subscription_duration_label),
        offerLabel: offer,
        objectives,
        requestPrecisions: nonEmpty(student.current_request_precisions),
        hasStudyProgram: student.has_study_program === true,
        profileSummary: nonEmpty(brief?.profile?.content?.profile_narrative),
        sessionBrief: toTutorStudentSessionBrief(brief, objectives.length > 1, ctx),
        game: toGame(student),
        details: toTutorStudentDetails(student, ctx),
        contacts: ctx.canSeeContacts
            ? {
                  email: `Elève : ${student.email ?? "-"} | Parent : ${student.parent_email ?? "-"}`,
                  phone: `Elève : ${student.mobile ?? "-"} | Parent : ${student.parent_mobile ?? "-"}`,
              }
            : null,
    }
}
